import { useFocusEffect } from '@react-navigation/native';
import { footerMarginBottomAtom, searchObjectQuery, showSearchBar } from 'app/atoms';
import { LogoCanada, LogoHauteGaspesie } from 'app/design/branding/logo';
import LogoMinister from 'app/design/images/hautegaspesie/logo-minister';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { LogoOneLine } from 'app/design/icons/logo-one-line';
import { LogoComponent } from 'app/lib/navigation';
import { useRoute } from 'app/hooks/navigation';
import { useUISize } from 'app/utils/web-helpers';
import { useMessageFooterContext } from 'app/screens/chat/message-footer-context';
import {
  BonificationPrompt,
  useBonificationBanner,
} from 'app/screens/billboard/all-listings/components/bonification-banner';
import { ExpiredLoans } from 'app/screens/billboard/all-listings/components/expired-loans';
import { useAtom, useAtomValue } from 'jotai';
import { t } from 'i18next';
import React, { Suspense, useCallback } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import Animated, { FadeOut, LinearTransition } from 'react-native-reanimated';
import { DASHBOARD_LAYOUT_MAX_WIDTH } from './dashboard-layout-constants';
import { createTheme as createDSTheme } from 'app/shopify';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { PartageClubFooter } from './partage-club-footer';
const MessageFooterOverlay = React.lazy(() =>
  import('app/screens/chat/message-footer').then((module) => ({ default: module.MessageFooterOverlay })),
);
const theme = createDSTheme();
export const AlertZindex = 20;
interface ExplorerLayoutProps {
  children: React.ReactNode;
  withMessageFooter?: boolean;
  fullWidth?: boolean;
}

export function FooterWithoutLinks() {
  const footerMargin = useAtomValue(footerMarginBottomAtom);
  const isHauteGaspesie = isWhiteLabel(WhiteLabel.HauteGaspesie);

  if (isHauteGaspesie) {
    return (
      <Column
        alignItems="center"
        backgroundColor="white-label.footer.footer-bg"
        paddingHorizontal="spacing.general.x3"
        paddingVertical="spacing.general.x16"
        style={{ width: '100%', marginTop: footerMargin }}
      >
        <Column alignItems="center" gap="spacing.general.x16">
          <LogoHauteGaspesie height={120} width={225} />

          <Row alignItems="flex-start" gap="spacing.general.x16">
            <Column alignItems="flex-start" gap="spacing.general.x1" width={165}>
              <Text color="text.inverse.primary" fontSize={12} fontWeight="600" letterSpacing={-0.24} lineHeight={16}>
                {t('poweredBy', { ns: 'splash' })}
              </Text>
              <LogoOneLine color={theme.colors['text.inverse.primary']} height={28} width={141} />
            </Column>

            <Column alignItems="flex-start" gap="spacing.general.x2">
              <Text
                color="text.inverse.primary"
                fontSize={12}
                fontWeight="600"
                letterSpacing={-0.24}
                lineHeight={16}
                numberOfLines={1}
              >
                {t('title', { ns: 'splash' })}
              </Text>
              <LogoMinister />
            </Column>
          </Row>
        </Column>
      </Column>
    );
  }

  if (!isWhiteLabel()) {
    return (
      <View style={{ marginTop: footerMargin }}>
        <PartageClubFooter />
      </View>
    );
  }

  return (
    <Column
      alignItems="center"
      backgroundColor="white-label.footer.footer-bg"
      paddingHorizontal="spacing.general.x3"
      paddingVertical="spacing.general.x2"
      style={{ width: '100%', marginTop: footerMargin }}
    >
      <Row alignItems="flex-start">
        <Column>
          <View style={{ left: -35 }}>
            <LogoComponent width="auto" height={130} />
          </View>
          <Row>
            <Column flex={1} alignItems="flex-start" gap="spacing.general.x2">
              <Column alignItems="flex-start" gap="spacing.general.x2">
                <Text color="white" style={{ left: 6 }}>
                  {t('poweredBy', { ns: 'splash' })}
                </Text>
                <LogoOneLine color="white" />
              </Column>
            </Column>
            {isWhiteLabel(WhiteLabel.Refrain) ? (
              <Column flex={1} alignItems="flex-start" gap="spacing.general.x2">
                <Text
                  fontSize={11}
                  maxWidth={220}
                  numberOfLines={2}
                  textAlign="left"
                  style={{ color: 'white' }}
                  variant="bodyBase"
                >
                  {t('title', { ns: 'splash' })}
                </Text>
                <LogoCanada height={(120 * 218) / 918} width={120} />
              </Column>
            ) : null}
          </Row>
        </Column>
      </Row>
    </Column>
  );
}

export function ExplorerLayout({ children, withMessageFooter, fullWidth = false }: ExplorerLayoutProps) {
  const [isShow, setShowSearchBar] = useAtom(showSearchBar);
  const searchQuery = useAtomValue(searchObjectQuery);
  const route = useRoute();
  const { isLargeUI } = useUISize();

  const shouldShowSearchBar = React.useMemo(() => {
    return (searchQuery.length > 0 || route.params !== undefined) && !isShow;
  }, [searchQuery.length, route.params, isShow]);

  useFocusEffect(
    useCallback(() => {
      if (shouldShowSearchBar) {
        setShowSearchBar(true);
      }
    }, [shouldShowSearchBar, setShowSearchBar]),
  );

  const { setHideConvo, setAllChannelsOpen } = useMessageFooterContext();
  const shouldShowDesktopAlerts = route.name === 'landing-page' && isLargeUI;
  const bannerState = useBonificationBanner({ enabled: shouldShowDesktopAlerts });
  const maxVisibleDesktopExpiredLoans = 2;

  return (
    <View style={styles.container}>
      {shouldShowDesktopAlerts ? (
        <View pointerEvents="box-none" style={styles.alertsOverlay}>
          <Column gap="spacing.general.x4" style={styles.alertsRail}>
            <Animated.View exiting={FadeOut.duration(180)} layout={LinearTransition.duration(250)}>
              <ExpiredLoans maxVisible={maxVisibleDesktopExpiredLoans} />
            </Animated.View>
            <Animated.View exiting={FadeOut.duration(180)} layout={LinearTransition.duration(250)}>
              <BonificationPrompt bannerState={bannerState} />
            </Animated.View>
          </Column>
        </View>
      ) : null}
      {withMessageFooter ? (
        <Suspense fallback={null}>
          <MessageFooterOverlay />
        </Suspense>
      ) : null}

      <View
        onPointerDown={() => {
          setHideConvo(true);
          setAllChannelsOpen(false);
        }}
        style={{
          maxWidth: fullWidth ? undefined : DASHBOARD_LAYOUT_MAX_WIDTH,
          alignSelf: 'center',
          width: '100%',
          flex: 1,
          minHeight: 0,
        }}
      >
        {children}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },
  alertsOverlay: {
    ...StyleSheet.absoluteFill,
    zIndex: AlertZindex,
  },
  alertsRail: {
    position: Platform.OS === 'web' ? ('fixed' as 'absolute') : 'absolute',
    right: theme.spacing['spacing.general.x8'],
    top: 60 + theme.spacing['spacing.general.x3'],
    width: 416,
  },
});
